import { CheckIcon, ChevronDown } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { FIELD_VARIANT } from '@avanta/shared/constants/forms';
import { cn } from '@avanta/shared/utils/cn';
import { Text } from '../Text';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/Command';
import { FieldIcon } from '../ui/FieldIcon';
import { Label } from '../ui/Label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/Popover';
import { ComboboxController } from './controller';
import { comboboxContent, comboboxItem, comboboxTrigger, comboboxWrapper } from './styles';
import { type ComboboxControllerProps, type ComboboxOption, type ComboboxProps } from './types';
import { getSelectedValues, translateOptionDescription, translateOptionLabel } from './utils';

export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>((props, ref) => {
  const {
    options,
    onBlur,
    name,
    label,
    helperText,
    required,
    placeholder = 'Combobox.placeholder',
    searchPlaceholder = 'Combobox.searchPlaceholder',
    emptyMessage = 'Combobox.empty',
    searchable = true,
    className,
    disabled = false,
    variant = FIELD_VARIANT.FILLED,
    leftIcon: LeftIcon,
    leftSlot: LeftSlot,
  } = props;
  const isMultiple = props.multiple === true;
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<string | string[]>(
    props.defaultValue ?? (isMultiple ? [] : '')
  );
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const isControlled = props.value !== undefined;
  const selectedValues = getSelectedValues(isMultiple, props.value, internalValue);
  const selectedValueList = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
  const selectedOptions = options.filter(option =>
    selectedValueList.includes(String(option.value))
  );
  const selectedOption = selectedOptions[0];
  const leftElement = LeftSlot || LeftIcon;
  const hasLeftIcon = LeftIcon || selectedOption?.icon;
  const selectedText =
    isMultiple && selectedOptions.length > 0
      ? t('Combobox.selection.label', {
          count: selectedOptions.length,
        })
      : selectedOptions.map(option => translateOptionLabel(t, option)).join(', ');

  const handleSelect = (currentValue: string): void => {
    if (isMultiple) {
      const currentValues = Array.isArray(selectedValues) ? selectedValues : [];
      const newValues = currentValues.includes(currentValue)
        ? currentValues.filter(value => value !== currentValue)
        : [...currentValues, currentValue];

      if (!isControlled) setInternalValue(newValues);
      if (props.multiple) props.onChange?.(newValues);
      return;
    }

    const selectedCurrentValue = typeof selectedValues === 'string' ? selectedValues : '';
    const newValue = currentValue === selectedCurrentValue ? '' : currentValue;
    if (!isControlled) setInternalValue(newValue);
    props.onChange?.(newValue);
    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean): void => {
    setOpen(isOpen);
    if (!isOpen) onBlur?.();
  };

  const isHelperTextVisible = !!helperText?.trim();

  return (
    <div className={comboboxWrapper} translate="no" data-variant={variant}>
      {label && <Label required={required}>{label}</Label>}
      <Popover modal open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            type="button"
            role="combobox"
            aria-controls={name}
            aria-expanded={open}
            disabled={disabled}
            data-has-value={selectedValueList.length > 0 && selectedValueList[0] !== ''}
            data-has-left-icon={hasLeftIcon ? '' : undefined}
            className={cn(comboboxTrigger, 'notranslate', className)}
            translate="no"
          >
            {(leftElement || selectedOption?.icon) && (
              <div className="absolute left-2 top-1/2 -translate-y-1/2">
                {hasLeftIcon ? (
                  <FieldIcon icon={LeftIcon || (selectedOption?.icon as React.ElementType)} />
                ) : (
                  LeftSlot
                )}
              </div>
            )}
            <span className="block truncate">
              {selectedOptions.length > 0 ? selectedText : t(placeholder)}
            </span>
            <ChevronDown strokeWidth={1.25} className="absolute right-2 size-6 text-denim-800" />
          </button>
        </PopoverTrigger>
        {name &&
          (isMultiple ? (
            selectedValueList.map(selectedValue => (
              <input key={selectedValue} type="hidden" name={name} value={selectedValue} />
            ))
          ) : (
            <input type="hidden" name={name} value={selectedValues} />
          ))}
        <PopoverContent className={comboboxContent}>
          <Command>
            {searchable && (
              <CommandInput placeholder={t(searchPlaceholder)} className="h-10 border-plum-200" />
            )}
            <CommandList aria-multiselectable={isMultiple || undefined}>
              <CommandEmpty>{t(emptyMessage)}</CommandEmpty>
              <CommandGroup className="p-0">
                {options.map(option => {
                  const isSelected = selectedValueList.includes(String(option.value));
                  const description = translateOptionDescription(t, option);

                  return (
                    <CommandItem
                      key={option.value}
                      value={String(option.value)}
                      className={cn(comboboxItem, 'h-auto min-h-10 px-3 py-1.75')}
                      onSelect={handleSelect}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'flex size-5 shrink-0 items-center justify-center rounded-md border',
                          isSelected ? 'border-teal-500 bg-mint-100' : 'border-gray-300 bg-white'
                        )}
                      >
                        {isSelected && <CheckIcon className="size-3.5 text-teal-500" />}
                      </span>
                      <div className="flex min-w-0 flex-1 flex-col items-start">
                        <span className="w-full wrap-break-words text-sm font-medium leading-4.5 text-denim-800">
                          {translateOptionLabel(t, option)}
                        </span>
                        {description && (
                          <span className="w-full wrap-break-words text-xs font-normal leading-4 text-gray-500">
                            {description}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {isHelperTextVisible && (
        <Text
          tag="span"
          variant="text-sm-light"
          className="group-data-[variant=invalid]/form:text-red-500"
        >
          {helperText}
        </Text>
      )}
    </div>
  );
});

Combobox.displayName = 'Combobox';

export { ComboboxController };
export type { ComboboxControllerProps, ComboboxOption, ComboboxProps };
